import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Live, ShowBranding } from '@liveforge/shared';
import { Button } from '../components/ui/button';
import { YouTubeCard } from '../components/youtube-card';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Field, FormError } from '../components/ui/field';
import { Modal } from '../components/ui/modal';
import { InlineLoader, LoadingScreen } from '../components/ui/spinner';
import { StatusBadge } from '../components/ui/status-badge';
import { useLives } from '../hooks/use-lives';
import { useShow } from '../hooks/use-shows';
import { errorMessage } from '../lib/api';
import { formatDateTime, fromDateTimeLocalValue } from '../lib/format';
import { createLive, deleteLive, updateShow } from '../lib/resources';

/** Empty strings are how a cleared text input arrives; the API wants null. */
function orNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function ShowDetailPage() {
  const { showId } = useParams<{ showId: string }>();
  const { show, loading, error, setShow } = useShow(showId);
  const { lives, loading: livesLoading, error: livesError, setLives } = useLives(showId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [backgroundUrl, setBackgroundUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#4f7cff');
  const [secondaryColor, setSecondaryColor] = useState('#101322');

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [creatingLive, setCreatingLive] = useState(false);
  const [liveTitle, setLiveTitle] = useState('');
  const [liveDescription, setLiveDescription] = useState('');
  const [liveScheduledAt, setLiveScheduledAt] = useState('');
  const [creatingBusy, setCreatingBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<Live | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Seed the form once the Show arrives, and again if it is reloaded.
  useEffect(() => {
    if (!show) {
      return;
    }
    setName(show.name);
    setDescription(show.description ?? '');
    setLogoUrl(show.branding.logoUrl ?? '');
    setBackgroundUrl(show.branding.backgroundUrl ?? '');
    setPrimaryColor(show.branding.primaryColor ?? '#4f7cff');
    setSecondaryColor(show.branding.secondaryColor ?? '#101322');
  }, [show]);

  if (loading) {
    return <LoadingScreen label="Loading show..." />;
  }

  if (!show) {
    return (
      <div className="empty-state">
        <h2>Show not available</h2>
        <p className="muted">{error ?? 'This show does not exist, or is not yours.'}</p>
        <Link className="button button--primary" to="/dashboard">
          Back to dashboard
        </Link>
      </div>
    );
  }

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!show) {
      return;
    }
    setSaveError(null);
    setSaved(false);
    setSaving(true);

    // Only the keys the form owns are sent. scenePreset is deliberately absent:
    // Phase 1 has no editor for it, so it must round-trip untouched.
    const branding: ShowBranding = {};
    if (orNull(logoUrl)) branding.logoUrl = logoUrl.trim();
    if (orNull(backgroundUrl)) branding.backgroundUrl = backgroundUrl.trim();
    branding.primaryColor = primaryColor;
    branding.secondaryColor = secondaryColor;

    try {
      const updated = await updateShow(show.id, {
        name: name.trim(),
        description: orNull(description),
        branding,
      });
      setShow(updated);
      setSaved(true);
    } catch (cause) {
      setSaveError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateLive(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!show) {
      return;
    }
    setCreateError(null);
    setCreatingBusy(true);
    try {
      const live = await createLive(show.id, {
        title: liveTitle.trim(),
        description: orNull(liveDescription),
        scheduledAt: fromDateTimeLocalValue(liveScheduledAt),
      });
      setLives([live, ...lives]);
      setCreatingLive(false);
      setLiveTitle('');
      setLiveDescription('');
      setLiveScheduledAt('');
    } catch (cause) {
      setCreateError(errorMessage(cause));
    } finally {
      setCreatingBusy(false);
    }
  }

  async function handleDeleteLive() {
    if (!pendingDelete) {
      return;
    }
    setDeleting(true);
    try {
      await deleteLive(pendingDelete.id);
      setLives(lives.filter((live) => live.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (cause) {
      setSaveError(errorMessage(cause));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <nav className="breadcrumb">
        <Link to="/dashboard">Shows</Link>
        <span aria-hidden="true">/</span>
        <span>{show.name}</span>
      </nav>

      <div className="page-header">
        <div>
          <h1>{show.name}</h1>
          <p className="page-header__meta muted">
            Created {formatDateTime(show.createdAt)} — updated {formatDateTime(show.updatedAt)}
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreatingLive(true)}>
          New live
        </Button>
      </div>

      {/*
        The lives are what this page is for, so they lead and take the wider
        column; the settings sit beside them instead of in front of them. One
        column again below 1000px, lives first.
      */}
      <div className="page-grid">
        <section>
          <div className="section-title">
            <h2>Lives</h2>
            <span className="mono muted">{lives.length}</span>
          </div>

          {livesError ? <FormError>{livesError}</FormError> : null}

          {livesLoading ? (
            <InlineLoader label="Loading lives..." />
          ) : lives.length === 0 ? (
            <div className="empty-state">
              <h3>No lives yet</h3>
              <p className="muted">A Live is one session produced from this Show.</p>
            </div>
          ) : (
            <div className="list">
              {lives.map((live) => (
                <div className="list__row" key={live.id}>
                  <div className="list__main">
                    <Link className="list__title" to={`/lives/${live.id}`}>
                      {live.title}
                    </Link>
                    <span className="list__subtitle muted">
                      {live.scheduledAt
                        ? `Scheduled for ${formatDateTime(live.scheduledAt)}`
                        : 'Not scheduled'}
                    </span>
                  </div>
                  <div className="list__actions row">
                    <StatusBadge status={live.status} />
                    <Button variant="ghost" small onClick={() => setPendingDelete(live)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="page-grid__aside">
          <section className="card">
            <div className="card__header">
              <h2>Settings</h2>
            </div>

            <form className="stack" onSubmit={handleSave}>
              <FormError>{saveError}</FormError>
              {saved ? <p className="alert alert--info">Saved.</p> : null}

              <Field label="Name">
                {(id) => (
                  <input
                    id={id}
                    className="input"
                    type="text"
                    required
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                )}
              </Field>

              <Field label="Description">
                {(id) => (
                  <textarea
                    id={id}
                    className="textarea"
                    rows={3}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                )}
              </Field>

              <h3 className="form-section">Branding</h3>

              <div className="form-grid">
                <Field label="Logo URL">
                  {(id) => (
                    <input
                      id={id}
                      className="input"
                      type="url"
                      placeholder="https://..."
                      value={logoUrl}
                      onChange={(event) => setLogoUrl(event.target.value)}
                    />
                  )}
                </Field>

                <Field label="Background URL">
                  {(id) => (
                    <input
                      id={id}
                      className="input"
                      type="url"
                      placeholder="https://..."
                      value={backgroundUrl}
                      onChange={(event) => setBackgroundUrl(event.target.value)}
                    />
                  )}
                </Field>

                <Field label="Primary colour">
                  {(id) => (
                    <div className="branding-preview">
                      <input
                        id={id}
                        className="input input--color"
                        type="color"
                        value={primaryColor}
                        onChange={(event) => setPrimaryColor(event.target.value)}
                      />
                      <span className="mono muted">{primaryColor}</span>
                    </div>
                  )}
                </Field>

                <Field label="Secondary colour">
                  {(id) => (
                    <div className="branding-preview">
                      <input
                        id={id}
                        className="input input--color"
                        type="color"
                        value={secondaryColor}
                        onChange={(event) => setSecondaryColor(event.target.value)}
                      />
                      <span className="mono muted">{secondaryColor}</span>
                    </div>
                  )}
                </Field>
              </div>

              <div className="form-actions">
                <Button type="submit" variant="primary" loading={saving} disabled={!name.trim()}>
                  Save changes
                </Button>
              </div>
            </form>
          </section>

          <YouTubeCard showId={show.id} />
        </div>
      </div>

      <Modal
        open={creatingLive}
        title="New live"
        onClose={() => setCreatingLive(false)}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setCreatingLive(false)}
              disabled={creatingBusy}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="create-live-form"
              type="submit"
              loading={creatingBusy}
              disabled={!liveTitle.trim()}
            >
              Create
            </Button>
          </>
        }
      >
        <form id="create-live-form" className="stack" onSubmit={handleCreateLive}>
          <FormError>{createError}</FormError>
          <Field label="Title">
            {(id) => (
              <input
                id={id}
                className="input"
                type="text"
                required
                autoFocus
                value={liveTitle}
                onChange={(event) => setLiveTitle(event.target.value)}
              />
            )}
          </Field>
          <Field label="Description" hint="Optional.">
            {(id) => (
              <textarea
                id={id}
                className="textarea"
                rows={2}
                value={liveDescription}
                onChange={(event) => setLiveDescription(event.target.value)}
              />
            )}
          </Field>
          <Field label="Scheduled for" hint="Optional. Your local time.">
            {(id) => (
              <input
                id={id}
                className="input"
                type="datetime-local"
                value={liveScheduledAt}
                onChange={(event) => setLiveScheduledAt(event.target.value)}
              />
            )}
          </Field>
        </form>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete live"
        message={
          pendingDelete
            ? `Delete "${pendingDelete.title}"? Its guest invites are deleted too. This cannot be undone.`
            : ''
        }
        busy={deleting}
        onConfirm={handleDeleteLive}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
